import React, { useRef, useEffect, useState, useCallback } from 'react';

const CanvasOrgChart = ({ treeData, onNodeClick, loading = false }) => {
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  
  // Node dimensions - optimized for compact display
  const NODE_WIDTH = 154; // Reduced from 220 by 30% (220 * 0.7 = 154)
  const NODE_HEIGHT = 90; // Keep same height
  const LEVEL_HEIGHT = 90; // Keep same level spacing
  const NODE_SPACING_X = 40; // Keep same horizontal spacing
  const NODE_SPACING_Y = 20; // Keep same vertical padding
  const CONNECTION_HEIGHT = 30; // Keep same connection height

  // Helper function to calculate tree width recursively
  const calculateTreeWidth = useCallback((node) => {
    if (!node.children || node.children.length === 0) {
      return NODE_WIDTH;
    }
    
    const childrenWidth = node.children.reduce((sum, child) => {
      return sum + calculateTreeWidth(child);
    }, 0);
    
    const spacingWidth = (node.children.length - 1) * NODE_SPACING_X;
    return Math.max(NODE_WIDTH, childrenWidth + spacingWidth);
  }, []);

  // Improved position calculation to prevent overlapping
  const calculateNodePositions = useCallback((nodes, level = 0, offsetX = 0) => {
    if (!nodes || nodes.length === 0) return [];
    
    const positions = [];
    let currentX = offsetX;
    
    nodes.forEach((node, index) => {
      const treeWidth = calculateTreeWidth(node);
      const nodeX = currentX + treeWidth / 2 - NODE_WIDTH / 2;
      
      const nodePos = {
        ...node,
        x: nodeX,
        y: level * (LEVEL_HEIGHT + NODE_SPACING_Y) + 50,
        level,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        treeWidth
      };
      
      positions.push(nodePos);
      
      // Calculate children positions centered under parent
      if (node.children && node.children.length > 0) {
        const totalChildrenWidth = node.children.reduce((sum, child) => {
          return sum + calculateTreeWidth(child);
        }, 0) + (node.children.length - 1) * NODE_SPACING_X;
        
        const childStartX = nodeX + NODE_WIDTH / 2 - totalChildrenWidth / 2;
        
        const childPositions = calculateNodePositions(
          node.children, 
          level + 1, 
          childStartX
        );
        positions.push(...childPositions);
      }
      
      currentX += treeWidth + NODE_SPACING_X;
    });
    
    return positions;
  }, [calculateTreeWidth]);

  // Helper function to fit text within available width
  const fitTextToWidth = useCallback((ctx, text, maxWidth) => {
    let displayText = text;
    let textWidth = ctx.measureText(displayText).width;
    
    if (textWidth > maxWidth && text.length > 3) {
      while (textWidth > maxWidth && displayText.length > 3) {
        displayText = text.substring(0, displayText.length - 4) + '...';
        textWidth = ctx.measureText(displayText).width;
      }
    }
    
    return displayText;
  }, []);

  // Draw functions
  const drawNode = useCallback((ctx, node) => {
    const { x, y, width, height, name, phone, level } = node;
    
    // Node background with level-based colors
    const colors = {
      1: { bg: '#277f4d', header: '#1e5a35', text: '#ffffff', body: '#ffffff' }, // Brand Green
      2: { bg: '#4a9d6a', header: '#277f4d', text: '#ffffff', body: '#ffffff' }, // Brand Green Light
      3: { bg: '#56b890', header: '#4a9d6a', text: '#ffffff', body: '#ffffff' }, // Brand Green Lighter
      default: { bg: '#277f4d', header: '#1e5a35', text: '#ffffff', body: '#ffffff' } // Brand Green
    };
    
    const color = colors[level] || colors.default;
    const cornerRadius = 6; // Reduced from 8
    
    // Draw rounded rectangle background
    ctx.fillStyle = color.body;
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, cornerRadius);
    ctx.fill();
    
    // Draw border with lighter shadow
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.08)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetY = 1;
    ctx.stroke();
    ctx.shadowColor = 'transparent'; // Reset shadow
    
    // Draw header with rounded top corners - reduced height
    ctx.fillStyle = color.header;
    ctx.beginPath();
    ctx.roundRect(x, y, width, 24, [cornerRadius, cornerRadius, 0, 0]); // Reduced from 32 to 24
    ctx.fill();
    
    // Draw level text in header - adjusted for smaller height
    ctx.fillStyle = color.text;
    ctx.font = 'bold 10px Arial, sans-serif'; // Reduced font size
    ctx.textAlign = 'center';
    ctx.fillText(`Level ${level}`, x + width / 2, y + 16); // Adjusted position for smaller header
    
    // Draw children count badge if any - ultra-compact badge
    if (node.children && node.children.length > 0) {
      const badgeX = x + width - 15; // Adjusted for narrower width
      const badgeY = y + 6;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.beginPath();
      ctx.roundRect(badgeX - 10, badgeY - 4, 20, 8, 4); // Even smaller badge
      ctx.fill();
      
      ctx.fillStyle = color.text;
      ctx.font = 'bold 7px Arial, sans-serif'; // Smaller font for narrow width
      ctx.textAlign = 'center';
      ctx.fillText(`${node.children.length}`, badgeX, badgeY + 2);
    }
    
    // Calculate available width for text (with padding)
    const availableWidth = width - 16; // 8px padding on each side
    
    // Draw name with smart text fitting
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 11px Arial, sans-serif';
    ctx.textAlign = 'center';
    const displayName = fitTextToWidth(ctx, name || 'No name', availableWidth);
    ctx.fillText(displayName, x + width / 2, y + 42);
    
    // Draw phone with smart text fitting
    ctx.fillStyle = '#6b7280';
    ctx.font = '9px Arial, sans-serif';
    ctx.textAlign = 'center';
    const displayPhone = fitTextToWidth(ctx, phone ? `${phone}` : 'No phone', availableWidth);
    ctx.fillText(displayPhone, x + width / 2, y + 58);
    
    // Draw city with smart text fitting
    if (node.city) {
      ctx.fillStyle = '#9ca3af';
      ctx.font = '8px Arial, sans-serif';
      ctx.textAlign = 'center';
      const displayCity = fitTextToWidth(ctx, node.city, availableWidth);
      ctx.fillText(displayCity, x + width / 2, y + 72);
    }
    
    // Draw small connection point at bottom - smaller size
    ctx.fillStyle = color.header;
    ctx.beginPath();
    ctx.arc(x + width / 2, y + height, 3, 0, 2 * Math.PI); // Reduced from 4 to 3
    ctx.fill();
  }, [fitTextToWidth]);

  const drawConnection = useCallback((ctx, parent, child) => {
    ctx.strokeStyle = '#9ca3af';
    ctx.lineWidth = 1.5; // Reduced from 2 to 1.5
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    const startX = parent.x + parent.width / 2;
    const startY = parent.y + parent.height;
    const endX = child.x + child.width / 2;
    const endY = child.y;
    
    // Calculate middle point for orthogonal lines - shorter connection
    const midY = startY + CONNECTION_HEIGHT / 2;
    
    ctx.beginPath();
    // Vertical line down from parent
    ctx.moveTo(startX, startY);
    ctx.lineTo(startX, midY);
    
    // Horizontal line to child's x position
    ctx.lineTo(endX, midY);
    
    // Vertical line down to child
    ctx.lineTo(endX, endY);
    ctx.stroke();
    
    // Draw smaller arrow at child connection point
    ctx.fillStyle = '#9ca3af';
    ctx.beginPath();
    ctx.moveTo(endX - 4, endY - 6); // Reduced arrow size
    ctx.lineTo(endX + 4, endY - 6);
    ctx.lineTo(endX, endY);
    ctx.closePath();
    ctx.fill();
  }, []);

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !treeData || treeData.length === 0) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Set canvas size
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Apply zoom and pan
    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);
    
    // Calculate positions with centering - reduced padding
    const rawPositions = calculateNodePositions(treeData, 0, 50); // Reduced padding from 100 to 50
    
    // Find bounds to center the chart
    const minX = Math.min(...rawPositions.map(p => p.x));
    const maxX = Math.max(...rawPositions.map(p => p.x + p.width));
    const totalWidth = maxX - minX;
    const canvasCenter = canvas.width / (window.devicePixelRatio * 2);
    const offsetX = canvasCenter - totalWidth / 2 - minX;
    
    // Apply centering offset
    const positions = rawPositions.map(pos => ({
      ...pos,
      x: pos.x + offsetX
    }));
    
    console.log('🎨 Canvas positions (centered):', positions);
    
    // Draw connections first - improved parent-child relationship mapping
    const drawConnections = (nodes, allPositions) => {
      nodes.forEach(node => {
        const parent = allPositions.find(p => p.id === node.id);
        if (parent && node.children && node.children.length > 0) {
          node.children.forEach(childData => {
            const child = allPositions.find(p => p.id === childData.id);
            if (child) {
              drawConnection(ctx, parent, child);
            }
          });
          // Recursively draw connections for children
          drawConnections(node.children, allPositions);
        }
      });
    };
    
    drawConnections(treeData, positions);
    
    // Draw nodes
    positions.forEach(node => {
      drawNode(ctx, node);
    });
    
    ctx.restore();
  }, [treeData, zoom, pan, calculateNodePositions, drawNode, drawConnection]);

  // Mouse event handlers
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - lastMousePos.x;
    const deltaY = e.clientY - lastMousePos.y;
    
    setPan(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));
    
    setLastMousePos({ x: e.clientX, y: e.clientY });
  }, [isDragging, lastMousePos]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.1, Math.min(3, prev * delta)));
  }, []);

  // Click handler for node selection
  const handleCanvasClick = useCallback((e) => {
    if (isDragging) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;
    
    const positions = calculateNodePositions(treeData);
    const clickedNode = positions.find(node => 
      x >= node.x && x <= node.x + node.width &&
      y >= node.y && y <= node.y + node.height
    );
    
    if (clickedNode && onNodeClick) {
      onNodeClick(clickedNode);
    }
  }, [isDragging, pan, zoom, treeData, calculateNodePositions, onNodeClick]);

  // Effect to redraw on data change
  useEffect(() => {
    drawChart();
  }, [drawChart]);

  // Zoom controls
  const zoomIn = () => setZoom(prev => Math.min(3, prev * 1.2));
  const zoomOut = () => setZoom(prev => Math.max(0.1, prev / 1.2));
  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tạo sơ đồ canvas...</p>
        </div>
      </div>
    );
  }

  if (!treeData || treeData.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V6a2 2 0 012-2h2a2 2 0 012 2v2m0 0V6a2 2 0 012-2h2a2 2 0 012 2v2m0 0v8a2 2 0 01-2 2H6a2 2 0 01-2-2V8" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Không có dữ liệu</h3>
          <p className="text-gray-500">Chưa có thông tin tổ chức để hiển thị</p>
        </div>
      </div>
    );
  }

  return (
    <div className="canvas-org-chart-container">
      {/* Controls */}
      <div className="mb-4 flex justify-between items-center">
        <div className="flex space-x-2">
          <button
            onClick={zoomIn}
            className="p-2 bg-brand-500 text-white rounded hover:bg-brand-600 flex items-center justify-center"
            title="Phóng to"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
          <button
            onClick={zoomOut}
            className="p-2 bg-brand-500 text-white rounded hover:bg-brand-600 flex items-center justify-center"
            title="Thu nhỏ"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
            </svg>
          </button>
          <button
            onClick={resetZoom}
            className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Mặc định
          </button>
          <button
            onClick={() => {
              // Try to get user phone from URL params or from treeData
              const urlParams = new URLSearchParams(window.location.search);
              const token = urlParams.get('token');
              let phoneNumber = '';
              
              if (token) {
                // If we have a token, try to extract phone from it
                try {
                  const payload = JSON.parse(atob(token.split('.')[1]));
                  phoneNumber = payload.phoneNumber || payload.phone || '';
                } catch (e) {
                  console.warn('Could not extract phone from token');
                }
              }
              
              // If no phone from token, try to get from treeData (first root node)
              if (!phoneNumber && treeData && treeData.length > 0) {
                phoneNumber = treeData[0].phone || treeData[0].id || '';
              }
              
              // If we still don't have phone, try to get from current URL path
              if (!phoneNumber) {
                const pathParts = window.location.pathname.split('/');
                const chartIndex = pathParts.indexOf('to-chuc-chart');
                if (chartIndex !== -1 && pathParts[chartIndex + 1]) {
                  phoneNumber = pathParts[chartIndex + 1];
                }
              }
              
              if (phoneNumber) {
                const shareUrl = `${window.location.origin}/to-chuc-chart/${phoneNumber}`;
                navigator.clipboard.writeText(shareUrl).then(() => {
                  alert('🔗 Link sơ đồ tổ chức đã được sao chép!\n\n' + shareUrl);
                }).catch(() => {
                  prompt('Sao chép link này để chia sẻ sơ đồ tổ chức:', shareUrl);
                });
              } else {
                alert('⚠️ Không thể tạo link chia sẻ. Vui lòng thử lại.');
              }
            }}
            className="px-3 py-1 bg-brand-500 text-white rounded hover:bg-brand-600 flex items-center space-x-1"
            title="Chia sẻ link xem sơ đồ tổ chức"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
            </svg>
            <span>Chia sẻ</span>
          </button>
        </div>
        <div className="text-sm text-gray-600">
        </div>
      </div>

      {/* Canvas */}
      <div className="border border-gray-300 rounded-lg overflow-hidden bg-gray-50">
        <canvas
          ref={canvasRef}
          className="w-full cursor-move bg-white"
          style={{ height: '600px', minHeight: '600px' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          onClick={handleCanvasClick}
        />
      </div>

      {/* Instructions */}
      <div className="mt-4 text-center text-sm text-gray-500">
        <p>🖱️ Kéo để di chuyển • 🔍 Scroll để zoom • 👆 Click node để xem chi tiết</p>
      </div>
    </div>
  );
};

export default CanvasOrgChart;
